import { useCallback, useEffect, useRef, useState } from 'react';

import { captureExceptionWithTeam } from 'Utilities/captureException/captureExceptionWithTeam';
import { TEAM } from 'Utilities/constants';

import { VehicleMotCheckResponseDetails } from '../@types/motCheck';
import { getCapitalizedMake } from '../Pages/CheckerResultsPage/CheckerResultsPage.helpers';
import { CHECK_PERFORMED_SERVICE_TYPE, CHECK_TOOLS_CATEGORIES_LIST, CHECKER_TOOLS_EVENTS, MOT_CHECK_KEY, safeLocalStorage } from '../Pages/CheckerRouter.utils';
import { getMOTChecker } from '../Utilities/Services/mot.api';
import { MOT_CHECK_STATUS_CODES } from '../Utilities/Services/motAPI.helpers';

import { useGetRouteAndPageName } from './useGetRouteAndPageName';
import { MAX_REQUEST_ATTEMPTS, POLLING_DURATION, POLLING_INTERVAL, sleep } from './useMotChecker.helper';

type useMotCheckProps = {
  onError: (code: number, vrm: string) => void;
  onSuccess: () => void;
}

type motCheckDataProp = VehicleMotCheckResponseDetails & { error: { message: string } };

export const useMotCheck = ({ onError, onSuccess }: useMotCheckProps) => {
  const { isMotCheckRoute, isTaxCheckRoute, routeInfo } = useGetRouteAndPageName();

  const statusCodeRef = useRef<number>(0);
  const terminatePolling = useRef<boolean>(false);
  const pollingExpirationTime = useRef<number>(0);
  const requestAttempts = useRef<number>(0);
  const [pollingStatus, setPollingStatus] = useState<string | undefined>();
  const [vehicleVRM, setVehicleVRM] = useState<string | undefined>();
  const controller: AbortController = new AbortController();

  const setStatusCode = (status?: number) => {
    if (status) {
      statusCodeRef.current = status;
    }
  };

  const processMotCheckDetails = useCallback(async (vrm: string, motCheckData: motCheckDataProp) => {
    const motCheckStatusCode = statusCodeRef.current;
    const isMotCheckStatusSuccess = motCheckStatusCode === MOT_CHECK_STATUS_CODES.STATUS_SUCCESS;

    // Added temporarily to capitalize make value
    const formattedMotCheckData = Object.fromEntries(
      Object.entries(motCheckData).map(([key, value]) => {
        if (typeof value !== 'string') {
          return [key, value];
        }

        return [key, key === 'make' ? getCapitalizedMake(value) : value.toLocaleLowerCase()];
      }),
    ) as motCheckDataProp;

    const { isMotValid, motValidUntil, taxStatus, taxValidUntil } = formattedMotCheckData || {};

    CHECKER_TOOLS_EVENTS.CHECK_PERFORMED({
      category: CHECK_TOOLS_CATEGORIES_LIST[routeInfo?.checkerPage as keyof typeof CHECK_TOOLS_CATEGORIES_LIST],
      serviceType: isMotCheckRoute ? CHECK_PERFORMED_SERVICE_TYPE.MOT : CHECK_PERFORMED_SERVICE_TYPE.TAX,
      status: isMotCheckStatusSuccess,
      value: JSON.stringify({
        ...((isMotCheckRoute && (isMotValid || motValidUntil)) && { isMotValid, motValidUntil }),
        ...((isTaxCheckRoute && (taxStatus || taxValidUntil)) && { taxStatus, taxValidUntil }),
      }),
      vrm,
    });

    if (isMotCheckStatusSuccess) {
      safeLocalStorage.setItem(MOT_CHECK_KEY, JSON.stringify({
        vrm,
        ...formattedMotCheckData,
      }));
      onSuccess?.();
    } else {
      const { error: { message: apiErrorMessage } } = formattedMotCheckData;
      captureExceptionWithTeam(`[MOT - MOT Check] - ${apiErrorMessage}`, TEAM.POWER_ENGAGERS_FE, {
        extra: {
          vrm,
        },
        level: 'warning',
      });
      onError(motCheckStatusCode, vrm);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onError, onSuccess]);

  const motCheckAction = useCallback(async (vrm: string) => {
    setVehicleVRM(vrm);
    while (!terminatePolling.current) {
      // Add intervals after 1st attempt
      if (requestAttempts.current === 1) {
        pollingExpirationTime.current = new Date().getTime() + POLLING_DURATION;
      }

      if (requestAttempts.current > MAX_REQUEST_ATTEMPTS) {
        setPollingStatus('MAX_ATTEMPTS_REACHED');
        break;
      }

      if (requestAttempts.current > 0 && new Date().getTime() > pollingExpirationTime.current) {
        setPollingStatus('REQUEST_EXPIRED');
        break;
      }

      const apiData = await getMOTChecker({
        opts: {
          abortController: controller,
        },
        setStatusCode,
        vrm,
      }).catch((error) => ({ error }));

      if (statusCodeRef.current === MOT_CHECK_STATUS_CODES.STATUS_PENDING) {
        await sleep(POLLING_INTERVAL);
        requestAttempts.current++;
      } else {
        setPollingStatus('SUCCESS');
        await processMotCheckDetails(vrm, apiData);
        break;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processMotCheckDetails]);

  useEffect(() => {
    const failedPollingStatus = pollingStatus && pollingStatus !== 'SUCCESS';

    if (failedPollingStatus && vehicleVRM) {
      captureExceptionWithTeam('[MOT - MOT Check] - Polling request pending', TEAM.POWER_ENGAGERS_FE, {
        extra: {
          pollingStatus,
          vrm: vehicleVRM,
        },
        level: 'warning',
      });

      onError(MOT_CHECK_STATUS_CODES.STATUS_PENDING, vehicleVRM);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingStatus, onError, vehicleVRM]);

  useEffect(() => () => {
    controller?.abort();
    terminatePolling.current = true;
    pollingExpirationTime.current = 0;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    motCheckAction,
    pollingStatus,
  };
};
